import { request } from './client.js';

export type AdminGenerationStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

export interface AdminGenerationSummaryDto {
  readonly id: string;
  readonly provider: 'wavespeed' | 'anthropic' | 'openai' | 'local';
  readonly modelId: string;
  readonly targetType: string;
  readonly targetId: string | null;
  readonly status: AdminGenerationStatus;
  readonly origin: 'pipeline' | 'admin_replay';
  readonly replayOfId: string | null;
  readonly promptPreview: string;
  readonly user: { id: string; email: string; name: string };
  readonly book: { id: string; title: string } | null;
  readonly latencyMs: number | null;
  readonly costUnits: string | null;
  readonly queuedAt: string;
  readonly finishedAt: string | null;
}

export interface AdminGenerationDetailDto extends AdminGenerationSummaryDto {
  readonly promptFinal: string | null;
  readonly promptParts: unknown;
  readonly negativePrompt: string | null;
  readonly refAssetIds: readonly string[];
  readonly seed: string | null;
  readonly sizeRequested: unknown;
  readonly requestPayload: unknown;
  readonly responsePayload: unknown;
  readonly httpStatus: number | null;
  readonly tokensIn: number | null;
  readonly tokensOut: number | null;
  readonly providerJobId: string | null;
  readonly error: string | null;
  readonly errorCode: string | null;
  readonly curl: string;
  readonly attempts: readonly {
    attempt: number;
    status: AdminGenerationStatus;
    requestPayload: unknown;
    responsePayload: unknown;
    providerJobId: string | null;
    httpStatus: number | null;
    latencyMs: number | null;
    error: string | null;
    errorCode: string | null;
    startedAt: string;
    finishedAt: string | null;
  }[];
  readonly replays: readonly AdminGenerationSummaryDto[];
  readonly comparison: readonly {
    id: string;
    status: AdminGenerationStatus;
    promptPreview: string;
    url: string | null;
    urlExpiresAt: string | null;
  }[];
  readonly referenceRevealCount: number;
}

export interface AdminGenerationFilters {
  provider?: string;
  modelId?: string;
  targetType?: string;
  status?: string;
  userId?: string;
  bookId?: string;
  from?: string;
  to?: string;
  sort?: 'time' | 'cost' | 'latency';
}

function query(filters: AdminGenerationFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
  const value = params.toString();
  return value ? `?${value}` : '';
}

export interface AdminQueueDto {
  readonly counts: {
    readonly queued: number;
    readonly running: number;
    readonly failed: number;
    readonly succeeded: number;
    readonly cancelled: number;
  };
  readonly failed: readonly {
    readonly id: string;
    readonly targetType: string;
    readonly bookId: string | null;
    readonly modelId: string;
    readonly errorCode: string | null;
    readonly error: string | null;
    readonly attempt: number;
    readonly finishedAt: string | null;
  }[];
}

export const adminGenerationsApi = {
  queue: () => request<AdminQueueDto>('/admin/queue'),
  list: (filters: AdminGenerationFilters = {}) =>
    request<{ items: readonly AdminGenerationSummaryDto[] }>(`/admin/generations${query(filters)}`),
  detail: (id: string) => request<AdminGenerationDetailDto>(`/admin/generations/${id}`),
  replay: (
    id: string,
    body: {
      promptFinal: string;
      negativePrompt?: string | null;
      modelId?: string;
      seed?: string;
      sizeRequested?: Record<string, unknown> | null;
      requestPayload?: Record<string, unknown>;
    },
  ) =>
    request<{ generationId: string; status: 'queued' }>(`/admin/generations/${id}/replay`, {
      method: 'POST',
      body,
    }),
  /**
   * Ретрай упавшей задачи: тот же `replay`, но без единой правки — все
   * параметры берутся из исходного обращения. Пустое тело обязательно:
   * `promptFinal: ''` схема не примет.
   */
  retry: (id: string) =>
    request<{ generationId: string; status: 'queued' }>(`/admin/generations/${id}/replay`, {
      method: 'POST',
      body: {},
    }),
  revealReferences: (id: string) =>
    request<{
      items: readonly { assetId: string; url: string; urlExpiresAt: string }[];
      auditedAt: string;
    }>(`/admin/generations/${id}/references/reveal`, { method: 'POST' }),
  copyCurl: (id: string) =>
    request<{ curl: string }>(`/admin/generations/${id}/curl`, { method: 'POST' }),
  apply: (id: string, sceneId: string) =>
    request<{ sceneId: string; variantId: string; assetId: string; applied: true }>(
      `/admin/generations/${id}/apply`,
      { method: 'POST', body: { sceneId } },
    ),
};
